import { useAppSelector } from '@Hooks/ReduxHooks'
import { CourseParentType, courseParentPath, coursePlayerPath } from '@Utilities/CoursePaths/coursePaths'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
type ICourseProvider = {
	course?: Course
	unit?: Unit
	tab?: Tab
	section?: TabSection
	classroomTabs: Tab[] | undefined
	selectedCourse?: string | null
	selectedUnit?: string | null
	selectedSection?: string | null
	selectedTab?: string | null
	classroomName?: string
	classroomLabel?: string
	currentUnits?: Unit[]
	// 'classroom' (default) or 'standalone' — controls where course navigation points
	parentType?: CourseParentType
}

type ICourseContext = ICourseProvider & {
	enrolled: boolean
	coursePivot?: CourseEnrollmentPivot
	isExpired: boolean
	expiredAt?: Date
	hasPrerequisites: boolean
	isClosed: boolean
	isFree: boolean
	navigateToCourse: () => void
	parentPath: string
}
const CourseContext = createContext<ICourseContext | null>(null)

export const useCourseContext = () => {
	const context = useContext(CourseContext)
	if (!context) {
		throw new Error('No Course Provider found')
	}
	return { ...context }
}

/** Same as useCourseContext but safe outside a CourseProvider (returns null). */
export const useOptionalCourseContext = () => {
	return useContext(CourseContext)
}

export const CourseProvider: React.FC<ICourseProvider> = ({ children, ...props }) => {
	const navigate = useNavigate()
	const { course } = props

	const { isAuth } = useAppSelector(state => state.auth)
	const enrolled = useMemo(() => {
		if (!isAuth || !course?.enrolled || (course.enrolled ?? []).length === 0 || !!course.enrolled?.[0]?.pivot?.pivot_deleted_at) return false
		return true
	}, [course])
	const coursePivot = useMemo<CourseEnrollmentPivot | undefined>(() => {
		if (!enrolled) return
		return course?.enrolled[0].pivot
	}, [enrolled, course])

	const isExpired = useMemo(() => {
		if (!enrolled) return false
		return (coursePivot && coursePivot?.pivot_expired) ?? false
	}, [coursePivot, enrolled])

	const expiredAt = useMemo(() => {
		return coursePivot && coursePivot.pivot_expire_at
	}, [isExpired])

	const hasPrerequisites = useMemo(() => {
		return (course && course.prerequisites && Object.keys(course.prerequisites).length > 0) ?? false
	}, [course])

	const isClosed = useMemo(() => {
		return (course && course.active && !course.buyable) ?? false
	}, [course])

	const isFree = useMemo(() => {
		return course?.price === 0
	}, [course])

	const parentType: CourseParentType = props.parentType ?? 'classroom'

	const navigateToCourse = () => {
		if (props.classroomLabel && course) {
			navigate(coursePlayerPath(parentType, props.classroomLabel, course.id))
		}
	}

	const parentPath = props.classroomLabel ? courseParentPath(parentType, props.classroomLabel) : '/'

	return (
		<CourseContext.Provider
			value={{ ...props, parentType, enrolled, isExpired, coursePivot, expiredAt, hasPrerequisites, isClosed, isFree, navigateToCourse, parentPath }}
		>
			{children}
		</CourseContext.Provider>
	)
}
